import { Handle, Position, useReactFlow } from '@xyflow/react';
import { formatDistanceToNow } from 'date-fns';
import { Users, MessageSquare, History, Settings, UserPlus, HelpCircle } from 'lucide-react';
import React, { memo, useState, useRef, KeyboardEvent, useEffect, useMemo } from 'react';
import { useUser } from '@/context/UserContext'; 
import { usePresenceState } from '@/hooks/usePresenceState';
import styles from '@/styles/NodePresence.module.css';
import NodeBaseContainer from '../base/base-container/NodeBaseContainer';

interface NodePresenceProps {
  id: string;
  data: any;
  selected: boolean;
}

const NodePresence: React.FC<NodePresenceProps> = ({ id, data, selected }) => {
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const user = useUser();
  const { updateNodeData } = useReactFlow();
  
  // Reference to track previous cursors state to avoid unnecessary updates
  const lastUserUpdateRef = useRef<number>(0);

  const {
    isLocked,
    isMinimized,
    minimizeLevel, // ✅ ADD: get minimizeLevel from hook
    activeTab,
    assignedEmails,
    activeUsers,
    chatMessages,
    handleLockChange,
    handleMinimizeChange,
    handleMinimizeLevelChange, // ✅ ADD: get minimizeLevel handler
    handleTitleChange,
    handleAssignUsersClick,
    handleTabChange,
    addChatMessage,
    syncNodeData
  } = usePresenceState({ 
    id, 
    data,
    updateNodeData
  });

  const [chatInput, setChatInput] = useState('');

  // ✅ ADD: Initialize minimizeLevel if not set
  useEffect(() => {
    if (data.minimizeLevel === undefined) {
      updateNodeData(id, { ...data, minimizeLevel: 'normal' });
    }
  }, [id, data, updateNodeData]);

  // ✅ ADD: Keep minifiedInfo in sync with source data
  useEffect(() => {
    const minifiedInfo: Record<string, any> = {};
    
    const activeUserCount = Object.keys(activeUsers).length;
    if (activeUserCount > 0) {
      minifiedInfo.users = `${activeUserCount} active user${activeUserCount !== 1 ? 's' : ''}`;
    }
    
    if (chatMessages.length > 0) {
      minifiedInfo.messages = `${chatMessages.length} message${chatMessages.length !== 1 ? 's' : ''}`;
    }
    
    const systemState = data.systemState || { lastCheck: Date.now(), isHealthy: true };
    minifiedInfo.status = systemState.isHealthy ? 'Healthy' : 'Issues';
    
    if (assignedEmails.length > 0) {
      minifiedInfo.assigned = `${assignedEmails.length} assigned`;
    }
    
    // Only update if there are actual changes
    const currentMinified = data.minifiedInfo || {};
    const hasChanges = JSON.stringify(currentMinified) !== JSON.stringify(minifiedInfo);
    
    if (hasChanges) {
      syncNodeData({ minifiedInfo });
    }
  }, [activeUsers, chatMessages.length, data.systemState, assignedEmails.length, data.minifiedInfo, syncNodeData]);

  useEffect(() => {
    const now = Date.now();
    const UPDATE_THRESHOLD = 500; // 5 seconds to prevent rapid updates

    // Skip updates if too recent
    if (now - lastUserUpdateRef.current < UPDATE_THRESHOLD) {
      return;
    }
    
    // Stringify for deep comparison
    const currentUsersString = JSON.stringify(data.activeUsers || {});
    const newUpdatedUsers = { ...data.activeUsers || {} };
    let hasChanges = false;

    // Add or update current user if logged in
    if (user.email) {
      const existingCurrentUser = newUpdatedUsers[user.email];
      const currentStatus = selected ? 'editing' : 'viewing';
      
      // Be very specific about currentNodeId - only set when actually on this node
      const newCurrentNodeId = selected ? id : null;
      
      const shouldUpdateCurrentUser =
        !existingCurrentUser ||
        existingCurrentUser.status !== currentStatus ||
        existingCurrentUser.currentNodeId !== newCurrentNodeId ||
        now - (existingCurrentUser.lastSeen || 0) > UPDATE_THRESHOLD;
        
      if (shouldUpdateCurrentUser) {
        newUpdatedUsers[user.email] = {
          email: user.email,
          currentNodeId: newCurrentNodeId,
          lastSeen: now,
          status: currentStatus,
        };
        hasChanges = true;
      }
    }
    
    // Clean up inactive users (older than 10 minutes)
    const INACTIVE_THRESHOLD = 10 * 60 * 1000; // Increase to 10 minutes
    Object.keys(newUpdatedUsers).forEach(email => {
      if (now - newUpdatedUsers[email].lastSeen > INACTIVE_THRESHOLD) {
        delete newUpdatedUsers[email];
        hasChanges = true;
      }
    });
    
    // Only sync if real changes occurred and after sufficient time passed
    if (hasChanges && JSON.stringify(newUpdatedUsers) !== currentUsersString) {
      syncNodeData({ activeUsers: newUpdatedUsers });
      lastUserUpdateRef.current = now;
    }
  }, [selected, user.email, id, syncNodeData]);

  // Scroll chat to bottom when new messages arrive
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Chat message submission handler
  const handleSubmitMessage = () => {
    if (chatInput.trim()) {
      // Add the message with proper user attribution
      console.log("user: ", user)
      addChatMessage(
        chatInput.trim(),
        user.email || 'current-user@example.com',
        Date.now()
      );
      
      // Clear the input field
      setChatInput('');
    }
  };

  // Handle Enter key in chat input
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitMessage();
    }
  };

  // Format timestamp to relative time
  const formatTimestamp = (timestamp: number): string => {
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  // Custom buttons for the node header
  const customButtons = [
    {
      id: '1',
      onClick: handleAssignUsersClick,
      icon: <UserPlus size={16} />,
      tooltip: 'Assign user(s)',
    },
    {
      id: '2',
      onClick: () => {}, // TODO: Implement node settings
      icon: <Settings size={16} />,
      tooltip: 'Settings',
    },
    {
      id: '3',
      onClick: () => {}, // TODO: Implement help guide
      icon: <HelpCircle size={16} />,
      tooltip: 'Help & Guide',
    },
  ];

  // Get the node system state
  const systemState = data.systemState || { lastCheck: Date.now(), isHealthy: true };

  // Compute status for NodeBaseContainer using useMemo
  const nodeStatus = useMemo(() => {
    if (!systemState.isHealthy) return "error";
    return "success";
  }, [systemState.isHealthy]);

  return (
    <NodeBaseContainer
      id={id}
      data={data}
      resizable={true}
      nodeTitle={data.label || "Project Presence"}
      isLocked={isLocked}
      isMinimized={isMinimized}
      minimizeLevel={minimizeLevel} // ✅ ADD: minimizeLevel prop
      onLockChange={handleLockChange}
      onMinimizeChange={handleMinimizeChange}
      onMinimizeLevelChange={handleMinimizeLevelChange} // ✅ ADD: minimize level change handler
      onTitleChange={handleTitleChange}
      customButtons={customButtons}
      assignedEmails={assignedEmails}
      onAssignUsersClick={handleAssignUsersClick}
      selected={selected}
      status={nodeStatus}
    >
      {/* ✅ FIX: Use minimizeLevel instead of isMinimized */}
      {minimizeLevel === 'normal' && (
        <div className={styles['presence-node']}>
          {/* System State Indicator */}
          <div className={styles['system-state']}>
            <div 
              className={`${styles['system-state-indicator']} ${
                systemState.isHealthy 
                  ? styles['system-state-healthy'] 
                  : styles['system-state-unhealthy']
              }`}
            />
            <span>
              {systemState.isHealthy ? 'System healthy' : 'System issues detected'} 
              {' • '} Last check: {formatTimestamp(systemState.lastCheck)}
            </span>
          </div>

          {/* Tab Navigation */}
          <div className={styles['tab-navigation']}>
            <button 
              className={`${styles['tab-button']} ${activeTab === 'users' ? styles.active : ''}`}
              onClick={() => handleTabChange('users')}
            >
              <Users size={16} className="mr-1" />
              Users
            </button>
            <button 
              className={`${styles['tab-button']} ${activeTab === 'chat' ? styles.active : ''}`}
              onClick={() => handleTabChange('chat')}
            >
              <MessageSquare size={16} className="mr-1" />
              Chat
            </button>
            <button 
              className={`${styles['tab-button']} ${activeTab === 'timeline' ? styles.active : ''}`}
              onClick={() => handleTabChange('timeline')}
            >
              <History size={16} className="mr-1" />
              Timeline
            </button>
          </div>

          {/* Tab Content */}
          <div className={styles['node-content']}>
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className={styles['user-list']}>
                {Object.keys(activeUsers).length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    No active users at the moment
                  </div>
                ) : (
                  Object.entries(activeUsers).map(([userId, user]) => (
                    <div key={userId} className={styles['user-item']}>
                      <div className={`${styles['user-status']} ${styles[user.status || 'online']}`} />
                      <div>
                        <div className="font-medium">{user.email}</div>
                        <div className="text-sm text-gray-500">
                          {user.currentNodeId 
                            ? <span className="text-blue-600">Viewing node: {user.currentNodeId}</span> 
                            : 'Not viewing any node'}
                          {' • '}
                          Last seen: {user.lastSeen ? formatTimestamp(user.lastSeen) : 'Unknown'}
                          {user.status && <> • Status: <span className="font-medium">{user.status}</span></>}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className={styles['chat-container']}>
                <div className={styles['messages-container']}>
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-gray-500 py-4">
                      No messages yet. Start the conversation!
                    </div>
                  ) : (
                    chatMessages.map((message) => (
                      <div 
                        key={message.id} 
                        className={`${styles.message} ${
                          message.email === user.email 
                            ? styles['message-self'] 
                            : styles['message-other']
                        }`}
                      >
                        <div className="text-xs font-medium mb-1">
                          {message.email === user.email ? 'You' : message.email}
                          {' • '}
                          {formatTimestamp(message.timestamp)}
                        </div>
                        <div>{message.content}</div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <div className={styles['chat-input-container']}>
                  <textarea
                    ref={chatInputRef}
                    className={styles['chat-input']}
                    placeholder="Type a message..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLocked}
                    rows={2}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Press Enter to send, Shift+Enter for new line
                  </div>
                </div>
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className={styles['timeline-container']}>
                <div className="text-center text-gray-500 py-4">
                  Timeline feature coming soon
                </div>
                {/* Placeholder for future timeline feature */}
                {/* 
                <div className={styles['timeline-item']}>
                  <div className={styles['timeline-marker']} />
                  <div className={styles['timeline-content']}>
                    <div className="font-medium">Event title</div>
                    <div className="text-sm text-gray-500">Event description</div>
                  </div>
                </div>
                */}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connection Handles */}
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
    </NodeBaseContainer>
  );
};

export default memo(NodePresence);